import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { testimonials } from '@/mocks/testimonials';
import { aboutFAQs } from '@/mocks/faqs';
import { injectStructuredData } from '@/components/feature/StructuredData';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [openFAQ, setOpenFAQ] = useState<number | null>(0);

  useEffect(() => {
    injectStructuredData([
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: '首页',
            item: 'https://peachhipguide.com/',
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: '关于我们',
          },
        ],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: '蜜桃臀塑形指南',
        url: 'https://peachhipguide.com/',
        description: '专注臀部塑形的专业健身内容平台，提供科学的训练方案和饮食建议。',
        inLanguage: 'zh-CN',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'AboutPage',
        name: '关于我们 - 蜜桃臀塑形指南',
        description: '了解蜜桃臀塑形指南的品牌故事、专业团队和用户口碑。',
        url: 'https://peachhipguide.com/about',
        inLanguage: 'zh-CN',
      },
    ]);
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.elements.namedItem('website_alt') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus('success');
      return;
    }

    const formData = new FormData(form);

    try {
      const response = await fetch('https://readdy.ai/api/form/d9slrl14ne17ei0sdjj0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg =
          parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        setFormError(serverMsg || '发送失败，请稍后重试');
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查连接后重试');
      setFormStatus('error');
    }
  };

  const stats = [
    { value: '50+', label: '原创文章' },
    { value: '6', label: '训练分类' },
    { value: '10万+', label: '月访问用户' },
    { value: '99%', label: '用户好评率' },
  ];

  return (
    <div className="min-h-screen pt-16">
      <div className="bg-background-100">
        <div className="max-w-5xl mx-auto px-4 md:px-6 py-8 md:py-12">
          <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '关于我们' }]} />

          <section className="mt-6 mb-14">
            <div className="text-center mb-10">
              <span className="inline-block px-3 py-1 bg-primary-100 text-primary-700 text-xs font-medium rounded-full mb-4 font-label">
                关于我们
              </span>
              <h1 className="text-2xl md:text-4xl font-heading font-semibold text-foreground-900 mb-4">
                蜜桃臀塑形指南
              </h1>
              <p className="text-foreground-600 text-sm max-w-2xl mx-auto leading-relaxed">
                我们是一个专注于臀部塑形的专业健身内容平台。无论你是健身新手还是资深训练者，
                无论你在家训练还是去健身房，这里都有适合你的科学训练方案。
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
              {stats.map((stat, i) => (
                <div
                  key={i}
                  className="bg-background-50 rounded-lg border border-background-200/60 p-5 text-center"
                >
                  <div className="text-2xl md:text-3xl font-heading font-bold text-primary-600 mb-1">
                    {stat.value}
                  </div>
                  <div className="text-xs text-foreground-600">{stat.label}</div>
                </div>
              ))}
            </div>
          </section>

          <section className="mb-14">
            <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-2 text-center">
              品牌故事
            </h2>
            <div className="bg-background-50 rounded-lg border border-background-200/60 p-6 md:p-8 max-w-3xl mx-auto">
              <p className="text-foreground-700 text-sm leading-relaxed mb-4">
                <strong>蜜桃臀塑形指南</strong>诞生于一群专业健身教练和运动康复师对臀部训练的热爱与研究。
                我们发现，网上关于练臀的信息虽然很多，但大部分碎片化、缺乏科学依据，
                甚至充斥着各种流传已久的错误认知。
              </p>
              <p className="text-foreground-700 text-sm leading-relaxed mb-4">
                于是，我们决定创建一个系统化、科学化的臀部训练内容平台。
                我们的每一篇文章都基于运动科学研究成果和教练团队的实际教学经验，
                力求让每一位读者都能在这里找到适合自己的训练方案。
              </p>
              <p className="text-foreground-700 text-sm leading-relaxed">
                我们相信，<strong>科学训练 + 合理饮食 + 坚持执行 = 理想的蜜桃臀</strong>。
                无论你的起点在哪里，只要用对方法并持续努力，每个人都能看到令人惊喜的改变。
              </p>
            </div>
          </section>

          <section className="mb-14">
            <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-2 text-center">
              核心团队
            </h2>
            <p className="text-foreground-600 text-sm text-center mb-8">
              由持证健身教练、运动康复师和注册营养师组成的专业团队
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 max-w-3xl mx-auto">
              {[
                {
                  name: '林教练',
                  role: '首席健身教练',
                  desc: 'NASM-CPT认证，专注女性塑形8年，累计指导学员2000+',
                },
                {
                  name: '李营养师',
                  role: '注册营养师',
                  desc: '中国注册营养师，运动营养专长，帮助1000+学员优化饮食方案',
                },
                {
                  name: '王康复师',
                  role: '运动康复师',
                  desc: '物理治疗博士，擅长运动损伤预防与体态矫正',
                },
              ].map((member) => (
                <div
                  key={member.name}
                  className="bg-background-50 rounded-lg border border-background-200/60 p-5 text-center"
                >
                  <div className="w-16 h-16 mx-auto mb-3 rounded-full bg-primary-100 flex items-center justify-center">
                    <i className="ri-user-3-line text-2xl text-primary-600"></i>
                  </div>
                  <h3 className="text-foreground-900 font-semibold text-sm mb-1">{member.name}</h3>
                  <p className="text-primary-600 text-xs font-medium mb-2">{member.role}</p>
                  <p className="text-foreground-600 text-xs leading-relaxed">{member.desc}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="mb-14">
            <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-2 text-center">
              用户口碑
            </h2>
            <p className="text-foreground-600 text-sm text-center mb-8">
              来自真实用户的训练反馈和成果分享
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {testimonials.map((t, i) => (
                <div
                  key={i}
                  className="bg-background-50 rounded-lg border border-background-200/60 p-5"
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-11 h-11 rounded-full overflow-hidden flex-shrink-0">
                      <img
                        src={t.avatar}
                        alt={t.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div>
                      <h4 className="text-foreground-900 font-medium text-sm">{t.name}</h4>
                      <p className="text-foreground-500 text-xs">{t.role}</p>
                    </div>
                    <div className="ml-auto flex items-center gap-0.5">
                      {Array.from({ length: t.rating }).map((_, j) => (
                        <i key={j} className="ri-star-fill text-accent-500 text-xs"></i>
                      ))}
                    </div>
                  </div>
                  <p className="text-foreground-700 text-xs leading-relaxed">{t.content}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="mb-14">
            <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-2 text-center">
              常见问答
            </h2>
            <div className="max-w-2xl mx-auto mt-6 flex flex-col gap-3">
              {aboutFAQs.map((faq, i) => (
                <div
                  key={i}
                  className="bg-background-50 border border-background-200/60 rounded-lg overflow-hidden"
                >
                  <button
                    className="w-full flex items-center justify-between gap-4 px-5 py-3 text-left cursor-pointer"
                    onClick={() => setOpenFAQ(openFAQ === i ? null : i)}
                  >
                    <span className="text-foreground-900 font-medium text-sm">{faq.question}</span>
                    <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 text-foreground-400">
                      <i className={`ri-${openFAQ === i ? 'subtract' : 'add'}-line`}></i>
                    </span>
                  </button>
                  {openFAQ === i && (
                    <div className="px-5 pb-4">
                      <div className="pt-2 border-t border-background-200/60">
                        <p className="text-foreground-600 text-sm leading-relaxed">{faq.answer}</p>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>

          <section className="mb-14">
            <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-2 text-center">
              联系我们
            </h2>
            <p className="text-foreground-600 text-sm text-center mb-8">
              有任何问题或建议？欢迎给我们留言
            </p>
            <div className="max-w-lg mx-auto">
              <form
                data-readdy-form
                id="contact-form"
                onSubmit={handleSubmit}
                className="bg-background-50 rounded-lg border border-background-200/60 p-6 flex flex-col gap-4"
              >
                <div>
                  <label htmlFor="name" className="block text-foreground-700 text-sm font-medium mb-1.5">
                    姓名
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    className="w-full px-4 py-2.5 bg-background-50 border border-background-200/60 rounded-md text-sm text-foreground-800 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 transition-colors"
                    placeholder="请输入您的姓名"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-foreground-700 text-sm font-medium mb-1.5">
                    邮箱
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    className="w-full px-4 py-2.5 bg-background-50 border border-background-200/60 rounded-md text-sm text-foreground-800 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 transition-colors"
                    placeholder="请输入您的邮箱"
                  />
                </div>
                <div>
                  <label htmlFor="message" className="block text-foreground-700 text-sm font-medium mb-1.5">
                    留言内容
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    maxLength={500}
                    rows={4}
                    className="w-full px-4 py-2.5 bg-background-50 border border-background-200/60 rounded-md text-sm text-foreground-800 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 transition-colors resize-none"
                    placeholder="请输入您的留言（最多500字）"
                  ></textarea>
                </div>

                <div className="form-honeypot-wrapper">
                  <input
                    type="text"
                    name="website_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />
                </div>

                <button
                  type="submit"
                  className="w-full py-3 bg-primary-500 text-background-50 font-semibold rounded-md hover:bg-primary-600 transition-colors text-sm whitespace-nowrap"
                >
                  发送留言
                </button>

                {formStatus === 'success' && (
                  <p className="text-sm text-green-600 text-center">感谢您的留言，我们会尽快回复！</p>
                )}
                {formStatus === 'error' && (
                  <p className="text-sm text-red-500 text-center">{formError}</p>
                )}
              </form>
            </div>
          </section>

          <div className="text-center pb-8">
            <Link
              to="/category/training-moves"
              className="inline-flex items-center gap-2 px-8 py-3.5 bg-primary-500 text-background-50 font-semibold rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap text-sm"
            >
              探索训练方案
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}